import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Heart, Archive, Sparkles, ShieldCheck, Cpu } from "lucide-react";
import { api, formatApiErrorDetail } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { BackArrow } from "@/components/BackArrow";
import { MatchChat } from "@/components/dashboard/MatchChat";
import { triggerMatchAlert } from "@/lib/matchAlert";

const ROBOT_A = "/robots/robot_a.png";
const ROBOT_B = "/robots/robot_b.png";

const T = {
  en: {
    title: "Avatar Conversation Viewer",
    sub: "Automated background chat between AI agents",
    breakdown: "AI Match Breakdown",
    compat: "Match Compatibility",
    why: "Why this works:",
    shared: "Shared objectives",
    myAvatar: "My Avatar", theirAvatar: "Their Avatar",
    dismiss: "Dismiss Connection",
    approve: "Approve & Send Notification",
    approved: "Approved — waiting for the other user",
    mutual: "Mutual Connection — human chat unlocked",
    dismissed: "Connection archived",
    disclaimer: "Once approved, your real profile chat opens. No direct contact data is shared until you type it yourself.",
    mutualToast: "IT'S A MATCH!",
    aiTag: "AI AGENTS — SIMULATED DIALOGUE",
  },
  es: {
    title: "Visor de Conversación de Avatares",
    sub: "Chat automatizado en segundo plano entre agentes de IA",
    breakdown: "Análisis del Match por IA",
    compat: "Compatibilidad del Match",
    why: "Por qué funciona:",
    shared: "Objetivos compartidos",
    myAvatar: "Mi Avatar", theirAvatar: "Su Avatar",
    dismiss: "Descartar Conexión",
    approve: "Aprobar y Enviar Notificación",
    approved: "Aprobado — esperando al otro usuario",
    mutual: "Conexión Mutua — chat humano desbloqueado",
    dismissed: "Conexión archivada",
    disclaimer: "Al aprobar, se abre el chat de tu perfil real. Ningún dato de contacto directo se comparte hasta que tú mismo lo escribas.",
    mutualToast: "¡ES UN MATCH!",
    aiTag: "AGENTES DE IA — DIÁLOGO SIMULADO",
  },
};

export default function MatchViewer() {
  const { id } = useParams();
  const { lang } = useLang();
  const t = T[lang] || T.en;
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/matches/${id}/detail`).then((r) => setM(r.data)).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  if (!m) return <div className="min-h-screen bg-[#05050A]" />;

  const fmtTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const isMutual = m.status === "Mutual Connection";

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${m.id}/like`);
      setM({ ...m, status: data.status, liked_by_me: true });
      if (data.status === "Mutual Connection") triggerMatchAlert(t.mutualToast, t.mutual);
      toast.success(data.status === "Mutual Connection" ? t.mutual : t.approved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post(`/matches/${m.id}/dismiss`);
      toast.info(t.dismissed);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex flex-col" data-testid="match-viewer-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#05050A]/80 border-b border-white/5 px-4 py-3 flex items-center gap-3">
        <BackArrow />
        <div className="min-w-0">
          <h1 className="font-heading text-sm sm:text-base font-black truncate" data-testid="match-viewer-title">{t.title}</h1>
          <p className="text-[10px] text-white/40 truncate">{t.sub}</p>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-6 pb-44">
        <section className="relative bg-[#0F0F1A]/90 border border-[#B026FF]/30 rounded-3xl p-6 mb-8 overflow-hidden" data-testid="match-breakdown-card">
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#B026FF] blur-[100px] opacity-25 pointer-events-none" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#D080FF] font-bold flex items-center gap-2 mb-4">
            <Cpu size={13} /> {t.breakdown}
          </p>
          <div className="flex items-center gap-5 mb-5">
            <p className="font-heading text-5xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] drop-shadow-[0_0_25px_rgba(0,255,170,0.5)]" data-testid="match-score">
              {m.compatibility}%
            </p>
            <div>
              <p className="text-xs font-bold text-white/80 uppercase tracking-widest">{t.compat}</p>
              <p className="text-[11px] text-white/40 mt-1">
                <Bot size={12} className="inline mr-1 text-[#00F5FF]" />{m.my_avatar_name} × {m.their_avatar_name}
              </p>
            </div>
          </div>
          <div className="bg-[#00FFAA]/5 border border-[#00FFAA]/25 rounded-2xl p-4" data-testid="match-analysis">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#00FFAA] font-bold mb-1.5 flex items-center gap-1.5"><Sparkles size={12} /> {t.why}</p>
            <p className="text-sm text-white/85 italic leading-relaxed">"{m.analysis_point}"</p>
          </div>
          {m.shared_objectives?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-[9px] uppercase tracking-widest text-white/35 font-bold">{t.shared}:</span>
              {m.shared_objectives.map((o) => (
                <span key={o} className="text-[10px] font-bold px-3 py-1 rounded-full bg-[#00F5FF]/10 border border-[#00F5FF]/30 text-[#00F5FF] uppercase">{o}</span>
              ))}
            </div>
          )}
        </section>

        <p className="text-center text-[9px] font-bold uppercase tracking-[0.3em] text-white/25 mb-5">— {t.aiTag} —</p>

        <section className="space-y-5" data-testid="match-transcript">
          {m.transcript.map((turn, i) => (
            <div key={i} className={`flex items-end gap-2.5 ${turn.mine ? "flex-row-reverse" : ""}`} data-testid={`transcript-bubble-${i}`}>
              <img src={turn.mine ? ROBOT_B : ROBOT_A} alt={turn.sender}
                className={`w-9 h-9 rounded-full shrink-0 object-cover border ${turn.mine ? "bg-[#B026FF]/20 border-[#B026FF]/50" : "bg-[#00F5FF]/20 border-[#00F5FF]/50"}`} />
              <div className={`max-w-[82%] ${turn.mine ? "text-right" : "text-left"}`}>
                <div className={`flex items-center gap-2 mb-1 ${turn.mine ? "justify-end" : ""}`}>
                  <span className={`text-[10px] font-bold ${turn.mine ? "text-[#D080FF]" : "text-[#00F5FF]"}`}>
                    {turn.mine ? t.myAvatar : t.theirAvatar} · {turn.sender}
                  </span>
                </div>
                <div className={`inline-block p-3.5 rounded-2xl text-sm text-white/90 text-left border ${
                  turn.mine
                    ? "bg-gradient-to-br from-[#B026FF]/25 to-[#B026FF]/5 border-[#B026FF]/25 rounded-br-sm shadow-[0_4px_18px_rgba(176,38,255,0.15)]"
                    : "bg-gradient-to-br from-[#00F5FF]/20 to-[#00F5FF]/5 border-[#00F5FF]/25 rounded-bl-sm shadow-[0_4px_18px_rgba(0,245,255,0.12)]"
                }`}>
                  {turn.text}
                </div>
                <div className={`flex items-center gap-2 mt-1.5 ${turn.mine ? "justify-end" : ""}`}>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${
                    turn.mine ? "bg-[#B026FF]/10 border-[#B026FF]/30 text-[#D080FF]" : "bg-[#00F5FF]/10 border-[#00F5FF]/30 text-[#00F5FF]"
                  }`} data-testid="tone-badge">[{turn.tone}]</span>
                  <span className="text-[9px] text-white/30" data-testid="bubble-timestamp">{fmtTime(turn.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
        </section>

        {isMutual && (
          <div className="mt-8" data-testid="viewer-human-chat">
            <MatchChat matchId={m.id} lang={lang} />
          </div>
        )}
      </main>

      <footer className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-[#05050A]/90 border-t border-white/10 px-4 pt-4 pb-5" data-testid="match-action-footer">
        <div className="max-w-2xl mx-auto">
          {isMutual ? (
            <p className="text-center text-xs font-bold text-[#00FFAA] py-3 flex items-center justify-center gap-2" data-testid="mutual-status">
              <Heart size={14} className="fill-current" /> {t.mutual}
            </p>
          ) : m.liked_by_me ? (
            <p className="text-center text-xs font-bold text-white/50 py-3 flex items-center justify-center gap-2" data-testid="approved-status">
              <Heart size={14} className="fill-current text-[#D080FF]" /> {t.approved}
            </p>
          ) : (
            <div className="flex gap-3">
              <button data-testid="dismiss-btn" onClick={dismiss} disabled={busy}
                className="flex-1 sm:flex-none sm:px-6 py-4 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white/5 border border-white/15 text-white/60 hover:bg-white/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                <Archive size={14} /> {t.dismiss}
              </button>
              <button data-testid="approve-btn" onClick={approve} disabled={busy}
                className="flex-[2] py-4 rounded-full text-[11px] font-black uppercase tracking-widest bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] text-[#05050A] shadow-[0_0_30px_rgba(0,255,170,0.55)] hover:shadow-[0_0_45px_rgba(0,255,170,0.8)] transition-shadow disabled:opacity-50 flex items-center justify-center gap-2 animate-pulse">
                <Heart size={15} className="fill-current" /> {t.approve}
              </button>
            </div>
          )}
          <p className="text-[9px] text-white/35 text-center mt-3 flex items-center justify-center gap-1.5" data-testid="footer-disclaimer">
            <ShieldCheck size={11} className="text-[#00FFAA] shrink-0" /> {t.disclaimer}
          </p>
        </div>
      </footer>
    </div>
  );
}
